import React from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { Account } from '../../interfaces/accounting.interface';

export const accountLabel = (a: Account): string => `${a.code} - ${a.name}`;

interface AccountAutocompleteProps {
  /** Accounts to choose from (caller may pass a pre-filtered subset, e.g. by type). */
  accounts: Account[];
  /** Selected account id ('' = none). */
  value: string;
  /** Called with the chosen account id, or '' when cleared. */
  onChange: (accountId: string) => void;
  label: string;
  required?: boolean;
  disabled?: boolean;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  error?: boolean;
  helperText?: string;
  /** Placeholder shown when empty — e.g. "use settings default". */
  placeholder?: string;
  'data-testid'?: string;
}

/**
 * Searchable Chart-of-Accounts picker. Replaces the plain `<Select>` used across the
 * accounting/sales/fixed-asset/expense forms: users can type part of an account code or
 * name to filter, so long charts stay usable and no leaf account is out of reach.
 *
 * The value in/out is the account id, so existing callers that store an id need no change.
 */
export const AccountAutocomplete: React.FC<AccountAutocompleteProps> = ({
  accounts,
  value,
  onChange,
  label,
  required,
  disabled,
  size = 'small',
  fullWidth = true,
  error,
  helperText,
  placeholder,
  ...rest
}) => {
  const selected = accounts.find(a => a.id === value) ?? null;
  return (
    <Autocomplete<Account>
      options={accounts}
      value={selected}
      onChange={(_e, val) => onChange(val?.id ?? '')}
      getOptionLabel={accountLabel}
      isOptionEqualToValue={(o, v) => o.id === v.id}
      disabled={disabled}
      fullWidth={fullWidth}
      size={size}
      autoHighlight
      renderInput={params => (
        <TextField
          {...params}
          label={label}
          required={required}
          error={error}
          helperText={helperText}
          placeholder={placeholder}
        />
      )}
      {...rest}
    />
  );
};

export default AccountAutocomplete;
